<script setup>
import { onMounted, onUnmounted, ref } from 'vue';

const props = defineProps({ onClose: Function });
const searchQuery = ref('');
const searchResults = ref(null);
const searchInput = ref(null);
const loading = ref(false);
let _prevBodyOverflow = '';

const close = () => {
    if (props.onClose) props.onClose();
};

const escapeListener = (e) => {
    if (e.key === 'Escape' || e.key === 'Esc') {
        close();
    }
};

let debounceTimer = null;
let latestRequestId = 0;

const search = () => {
    searchResults.value = null;
    let q = searchQuery.value.trim();

    // cancel any pending debounced call so only the most recent keystroke schedules a fetch
    if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
    }

    if (q.length < 2) {
        return;
    }

    debounceTimer = setTimeout(async () => {
        if (q !== searchQuery.value.trim()) {
            // query changed, ignore this result
            return;
        }

        // claim this request's id so we can tell later if a newer one has since started
        const requestId = ++latestRequestId;

        loading.value = true;

        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/search/?q=${encodeURIComponent(q)}&type=all`);

        // a newer request has since been issued; drop this stale response entirely
        if (requestId !== latestRequestId) {
            return;
        }

        if (res && res.ok) {
            searchResults.value = await res.json();
        } else {
            searchResults.value = null;
        }

        loading.value = false;
    }, 250);
}

const getId = (result) => {
    if (result.anilist_id) {
        return result.anilist_id;
    }

    if (result.ja) {
        return `ja:${encodeURIComponent(result.ja).replace(/\%2F/g, '%252F')}`;
    }
    if (result.en) {
        return `en:${encodeURIComponent(result.en).replace(/\%2F/g, '%252F')}`;
    }
    return null;
};

const getStudios = (staff) => {
    const studios = staff?.studios;
    if (!studios || studios.length === 0) {
        return null;
    }
    
    const keys = Object.keys(studios);
    const seen = new Set();
    return keys.filter(key => {
        const lower = key.toLowerCase();
        if (seen.has(lower)) {
            return false;
        }
        seen.add(lower);
        return true;
    }).join(', ');
};

const getJobs = (staff) => {
    const jobs = staff?.jobs;
    if (!jobs || jobs.length === 0) {
        return null;
    }
    return jobs.join(', ');
};

const getImage = (staff) => {
    if (staff?.avatar && staff?.anilist_id) {
        const prefix = staff.is_studio === 1 ? '0' : '1';
        return `/assets/img/staff/${prefix}-${staff.anilist_id}/${staff.avatar}.jpg`;
    }
    return null;
};

// Block page scrolling while this overlay is mounted, but allow scrolling
// inside the results container (it already has overflow-y-auto).
onMounted(() => {
    // save previous value so we can restore it
    _prevBodyOverflow = document.body.style.overflow || '';
    document.body.style.overflow = 'hidden';
    // close on Escape
    document.addEventListener('keydown', escapeListener);

    // focus the input
    setTimeout(() => {
        if (searchInput?.value) {
            searchInput.value.focus();
        }
    });
});

onUnmounted(() => {
    if (escapeListener) {
        document.removeEventListener('keydown', escapeListener);
    }
    if (debounceTimer) {
        clearTimeout(debounceTimer);
    }
    // restore previous overflow
    document.body.style.overflow = _prevBodyOverflow || '';
});
</script>

<template>
    <div class="fixed w-full h-full overflow-y-auto top-0 left-0 p-[calc(min(5rem,10%))] bg-black/70 z-50" @click.self="close">
        <div class="flex flex-col items-center justify-start gap-6 mx-auto max-w-[54em]">
            <input
                type="search"
                :placeholder="$t('common.searchPlaceholder')"
                class="w-full input dark:!bg-app-surface2 !rounded-full !px-5 !py-3"
                v-model="searchQuery"
                @input="search"
                autofocus
                ref="searchInput"
            />

            <p class="w-full text-left text-gray-400 -mt-4 text-xs">
                <i18n-t keypath="search.subtitle">
                    <template #advancedSearchLink>
                        <router-link to="/search" class="underline">
                            {{ $t('search.advancedSearch') }}
                        </router-link>
                    </template>
                </i18n-t>
            </p>

            <div v-if="loading" class="w-full flex flex-col gap-4 justify-center items-center text-gray-200 dark:text-gray-600">
                <i class="text-6xl">
                    <font-awesome-icon icon="fa-solid fa-circle-notch" spin />
                </i>
                <p>{{ $t('common.loading') }}</p>
            </div>
            <div v-else-if="searchResults" class="flex flex-col sm:flex-row gap-6 w-full justify-center">

                <div v-if="searchResults.stafflists && searchResults.stafflists.length" class="w-full sm:flex-1 min-w-0">
                    <h5 class="text-white">{{ $t('search.categories.staffLists') }}</h5>

                    <div class="bg-app-surface2 rounded-md overflow-y-auto shadow-lg max-h-[calc(100vh-10rem)] sm:max-h-96">
                        <ul class="divide-y divide-app-border">
                            <li v-for="stafflist in searchResults.stafflists" :key="stafflist.anilist_id" class="hover:bg-app-surface3">
                                <RouterLink :to="`/staff/${stafflist.slug}`" class="flex items-center gap-3 px-4 py-2">
                                    <div v-if="stafflist.kv" class="inline-block w-9 h-12 bg-gray-100 bg-cover bg-center rounded-md overflow-hidden flex-shrink-0" :class="{ 'nsfw': stafflist.isAdult == 1 }" :style="{ backgroundImage: `url('${stafflist.kv}')` }"></div>
                                    <span v-if="stafflist.name" class="truncate">{{ stafflist.name }}</span>
                                </RouterLink>
                            </li>
                        </ul>
                    </div>
                </div>

                <div v-if="searchResults.staff && searchResults.staff.length" class="w-full sm:flex-1 min-w-0">
                    <h5 class="text-white">{{ $t('search.categories.staff') }}</h5>

                    <div class="bg-app-surface2 rounded-md overflow-y-auto shadow-lg max-h-[calc(100vh-10rem)] sm:max-h-96">
                        <ul class="divide-y divide-app-border">
                            <li v-for="staff in searchResults.staff" :key="staff.anilist_id" class="hover:bg-app-surface3">
                                <RouterLink :to="`/${staff.is_studio === 1 ? 'studio' : 'person'}/${getId(staff)}`" class="flex flex-col xs:flex-row md:items-center gap-3 px-4 py-3" :class="{'font-bold': staff.is_studio === 1}">
                                    <img v-if="getImage(staff)" :src="getImage(staff)" alt="Avatar" class="inline-block w-12 h-12 object-cover mx-auto xs:m-0 rounded-md flex-shrink-0">
                                    <div class="flex-1 min-w-0">
                                        <div class="flex flex-col md:flex-row md:items-center md:gap-2">
                                            <div class="flex items-center gap-2">
                                                <font-awesome-icon v-if="staff.is_studio === 1" icon="fa-solid fa-briefcase" class="text-slate-400 text-xs" />
                                                <span v-if="staff.en">{{ staff.en }}</span>
                                            </div>
                                            <span v-if="staff.ja" class="flex gap-1">
                                                <span class="text-slate-400">(</span>{{ staff.ja }}<span class="text-slate-400">)</span>
                                            </span>
                                        </div>
                                        <div v-if="getStudios(staff) && staff.is_studio !== 1" class="text-xs italic text-gray-400 leading-tight mt-1 truncate" :title="getStudios(staff)">
                                            ({{ getStudios(staff) }})
                                        </div>
                                        <div v-if="getJobs(staff)" class="mt-1 text-xs text-gray-500 leading-tight font-normal">
                                            <i18n-t keypath="search.jobsText">
                                                <template #jobs>
                                                    <strong>{{ $t('search.jobs') }}</strong>
                                                </template>
                                                <template #count>
                                                    {{ getJobs(staff) }}
                                                </template>
                                            </i18n-t>
                                        </div>
                                    </div>
                                </RouterLink>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>